import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Redirect } from "wouter";
import { Megaphone, ExternalLink, Loader2, ShieldCheck, Store } from "lucide-react";
import { requestPartnerAdImageUploadUrl } from "@workspace/api-client-react";
import type {
  PartnerAdBusinessType,
  PartnerAdCampaignInput,
  PartnerAdCampaignSummary,
  PartnerAdPreferencesUpdate,
  PartnerAdsDashboard,
} from "@workspace/api-client-react";
import { useAuth } from "@/context/auth";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const INDUSTRIES: Array<{ value: PartnerAdBusinessType; label: string }> = [
  { value: "supermarket", label: "Supermarkets" },
  { value: "fashion", label: "Fashion" },
  { value: "ready_to_wear", label: "Ready-to-wear" },
  { value: "food", label: "Food & restaurants" },
  { value: "salon", label: "Salons & beauty" },
  { value: "tailor", label: "Tailoring" },
  { value: "pharmacy", label: "Pharmacies" },
  { value: "gadget_repair", label: "Gadget repair" },
  { value: "building", label: "Building supplies" },
  { value: "laundry", label: "Laundry" },
  { value: "fabric_textile", label: "Fabric & textiles" },
  { value: "general_retail", label: "General retail" },
];

const industryLabel = (value: string) =>
  INDUSTRIES.find(industry => industry.value === value)?.label ?? value;

function localDateTimeInput(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || "The request could not be completed.");
  }
  return response.json() as Promise<T>;
}

function uploadPartnerAdImage(uploadURL: string, file: File, onProgress: (progress: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", uploadURL);
    request.setRequestHeader("Content-Type", file.type);
    request.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onload = () => request.status >= 200 && request.status < 300
      ? resolve()
      : reject(new Error("The flyer upload could not be completed. Try again."));
    request.onerror = () => reject(new Error("The connection was interrupted during upload. Try again."));
    request.send(file);
  });
}

function statusLabel(campaign: PartnerAdCampaignSummary): string {
  if (campaign.status === "approved" && new Date(campaign.endsAt).getTime() < Date.now()) return "Ended";
  if (campaign.status === "approved" && new Date(campaign.startsAt).getTime() > Date.now()) return "Scheduled";
  return campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1);
}

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  if (status === "approved") return "default";
  if (status === "rejected") return "destructive";
  if (status === "pending") return "secondary";
  return "outline";
}

export default function PartnerAdsPage() {
  const { user, store } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isManager = user?.role === "admin" || user?.role === "manager";
  const isSchoolCampus = user?.businessType === "school_campus";
  const queryKey = ["partner-ads-dashboard", store?.id];
  const dashboard = useQuery({
    queryKey,
    enabled: Boolean(isManager && store?.id && !isSchoolCampus),
    queryFn: () => requestJson<PartnerAdsDashboard>("/api/partner-ads/dashboard"),
  });

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [ctaLabel, setCtaLabel] = useState("Learn more");
  const [targetIndustryTypes, setTargetIndustryTypes] = useState<PartnerAdBusinessType[]>([]);
  const [imageObjectPaths, setImageObjectPaths] = useState<string[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [startsAt, setStartsAt] = useState(() => localDateTimeInput(new Date(Date.now() + 60 * 60 * 1000)));
  const [endsAt, setEndsAt] = useState(() => localDateTimeInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)));

  const handleImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = "";
    if (!files.length) return;
    if (imageObjectPaths.length + files.length > 2) {
      toast({ title: "Choose up to two flyer images", variant: "destructive" });
      return;
    }
    const invalidFile = files.find(file =>
      !["image/jpeg", "image/png", "image/webp"].includes(file.type)
      || file.size <= 0
      || file.size > 5 * 1024 * 1024,
    );
    if (invalidFile) {
      toast({
        title: "Unsupported flyer image",
        description: "Use a JPG, PNG or WebP image that is 5 MB or smaller.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingImages(true);
    setUploadProgress(0);
    try {
      for (const [index, file] of files.entries()) {
        const signed = await requestPartnerAdImageUploadUrl(
          { name: file.name, size: file.size, contentType: file.type },
          { credentials: "include" },
        );
        await uploadPartnerAdImage(signed.uploadURL, file, progress => {
          setUploadProgress(Math.round(((index + progress / 100) / files.length) * 100));
        });
        setImageObjectPaths(current => [...current, signed.objectPath]);
        setUploadProgress(Math.round(((index + 1) / files.length) * 100));
      }
    } catch (error) {
      toast({
        title: "Could not upload flyer image",
        description: error instanceof Error ? error.message : "Try again with another image.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingImages(false);
    }
  };

  const savePreferences = useMutation({
    mutationFn: (preferences: PartnerAdPreferencesUpdate) =>
      requestJson("/api/partner-ads/preferences", {
        method: "PUT",
        body: JSON.stringify(preferences),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast({ title: "Offer preferences saved" });
    },
    onError: (error: Error) => toast({ title: "Could not save preferences", description: error.message, variant: "destructive" }),
  });

  const createCampaign = useMutation({
    mutationFn: (payload: PartnerAdCampaignInput) =>
      requestJson("/api/partner-ads/campaigns", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      setTitle("");
      setDescription("");
      setDestinationUrl("");
      setCtaLabel("Learn more");
      setTargetIndustryTypes([]);
      setImageObjectPaths([]);
      setUploadProgress(0);
      toast({ title: "Campaign submitted", description: "It will appear after Super Admin review." });
    },
    onError: (error: Error) => toast({ title: "Could not submit campaign", description: error.message, variant: "destructive" }),
  });

  if (isSchoolCampus) {
    const hasSchoolFeesAccess = user?.role === "admin" || user?.role === "superadmin"
      || user?.permissions?.includes("school_fees");
    return <Redirect to={hasSchoolFeesAccess ? "/school-fees" : "/"} />;
  }

  if (!isManager) {
    return <div className="mx-auto max-w-3xl p-6"><p role="alert">Partner offers are available to store owners and managers.</p></div>;
  }

  const preferences = dashboard.data?.preferences ?? { enabled: false, blockedCategories: [] };
  const campaigns = dashboard.data?.campaigns ?? [];

  const submitCampaign = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const start = new Date(startsAt);
    const end = new Date(endsAt);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start || end <= new Date()) {
      toast({ title: "Check the campaign schedule", description: "The end time must be after the start time and in the future.", variant: "destructive" });
      return;
    }
    try {
      const url = new URL(destinationUrl);
      if (url.protocol !== "https:") throw new Error("Use a secure HTTPS link.");
    } catch (error) {
      toast({ title: "Enter a valid campaign link", description: error instanceof Error && error.message !== "Invalid URL" ? error.message : "Campaign links must start with https://.", variant: "destructive" });
      return;
    }
    if (isUploadingImages) {
      toast({ title: "Wait for flyer uploads to finish", variant: "destructive" });
      return;
    }
    createCampaign.mutate({
      title: title.trim(),
      description: description.trim(),
      destinationUrl: destinationUrl.trim(),
      ctaLabel: ctaLabel.trim(),
      targetIndustryTypes,
      imageObjectPaths,
      startsAt: start.toISOString(),
      endsAt: end.toISOString(),
    });
  };

  const updateBlockedCategories = (category: PartnerAdBusinessType, checked: boolean) => {
    const blockedCategories = checked
      ? [...new Set([...preferences.blockedCategories, category])]
      : preferences.blockedCategories.filter(value => value !== category);
    savePreferences.mutate({ enabled: preferences.enabled, blockedCategories });
  };

  return (
    <main className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Partner offers</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Submit a promotion to other participating RetailPulsePOS businesses, or choose whether your dashboard hosts offers.
          </p>
        </div>
        <Badge variant="outline">Pilot</Badge>
      </header>

      {dashboard.isLoading && (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground" role="status">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading partner offers…
        </div>
      )}
      {dashboard.isError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm" role="alert">
          <p>{dashboard.error instanceof Error ? dashboard.error.message : "Partner offers could not be loaded."}</p>
          <Button className="mt-3" variant="outline" size="sm" onClick={() => dashboard.refetch()}>Try again</Button>
        </div>
      )}

      {dashboard.data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><Store className="h-5 w-5" /> Host offers on your dashboard</CardTitle>
              <CardDescription>
                Your choice is store-wide. Offers are only shown to managers and owners, never in checkout or on store screens.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4">
                <div>
                  <Label htmlFor="partner-ad-opt-in" className="font-medium">Allow approved partner offers</Label>
                  <p className="mt-1 text-sm text-muted-foreground">Opt in to browse eligible partner offers one at a time in a slider on your merchant dashboard.</p>
                </div>
                <Switch
                  id="partner-ad-opt-in"
                  checked={preferences.enabled}
                  disabled={savePreferences.isPending}
                  onCheckedChange={enabled => savePreferences.mutate({ enabled, blockedCategories: preferences.blockedCategories })}
                  aria-label="Allow partner offers on this store's dashboard"
                />
              </div>
              <fieldset className="space-y-3">
                <legend className="text-sm font-medium">Categories to block</legend>
                <p className="text-sm text-muted-foreground">Blocked categories will not appear, even when your store is opted in.</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {INDUSTRIES.map(industry => (
                    <label key={industry.value} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                      <Checkbox
                        checked={preferences.blockedCategories.includes(industry.value)}
                        disabled={savePreferences.isPending}
                        onCheckedChange={checked => updateBlockedCategories(industry.value, checked === true)}
                      />
                      {industry.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Submit an offer</CardTitle>
              <CardDescription>Every submission is reviewed before it can appear to participating stores.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-5" onSubmit={submitCampaign}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="partner-ad-title">Offer title</Label>
                    <Input id="partner-ad-title" value={title} onChange={event => setTitle(event.target.value)} minLength={3} maxLength={90} required placeholder="e.g. 10% off your first order" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="partner-ad-url">Secure destination link</Label>
                    <Input id="partner-ad-url" type="url" value={destinationUrl} onChange={event => setDestinationUrl(event.target.value)} maxLength={512} required placeholder="https://example.com/offer" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="partner-ad-description">Short description</Label>
                  <Textarea id="partner-ad-description" value={description} onChange={event => setDescription(event.target.value)} minLength={10} maxLength={260} required rows={3} placeholder="Explain the offer and any key conditions." />
                  <p className="text-xs text-muted-foreground">{description.length}/260 characters</p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="partner-ad-cta">Button label</Label>
                    <Input id="partner-ad-cta" value={ctaLabel} onChange={event => setCtaLabel(event.target.value)} minLength={2} maxLength={24} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="partner-ad-start">Starts</Label>
                    <Input id="partner-ad-start" type="datetime-local" value={startsAt} onChange={event => setStartsAt(event.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="partner-ad-end">Ends</Label>
                    <Input id="partner-ad-end" type="datetime-local" value={endsAt} onChange={event => setEndsAt(event.target.value)} required />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="partner-ad-flyers">Flyer images (optional)</Label>
                    <p className="text-sm text-muted-foreground">
                      Add up to two JPG, PNG or WebP images. Each must be 5 MB or smaller and at least 500 px on one side and 300 px on the other.
                    </p>
                  </div>
                  <Input
                    id="partner-ad-flyers"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    disabled={isUploadingImages || createCampaign.isPending || imageObjectPaths.length >= 2}
                    onChange={event => void handleImageSelection(event)}
                  />
                  {isUploadingImages && (
                    <div className="space-y-1" role="status">
                      <p className="text-sm text-muted-foreground">Uploading flyer images… {uploadProgress}%</p>
                      <progress className="h-2 w-full accent-primary" value={uploadProgress} max={100} aria-label="Flyer upload progress" />
                    </div>
                  )}
                  {imageObjectPaths.length > 0 && (
                    <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label="Selected flyer images">
                      {imageObjectPaths.map((path, index) => (
                        <div key={path} className="space-y-2 rounded-md border p-2">
                          <img
                            src={`${BASE}/api/storage${path}`}
                            alt={`Flyer image ${index + 1} preview`}
                            className="aspect-video w-full rounded bg-muted object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isUploadingImages || createCampaign.isPending}
                            aria-label={`Remove flyer image ${index + 1}`}
                            onClick={() => setImageObjectPaths(current => current.filter(item => item !== path))}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{imageObjectPaths.length} of 2 images added</p>
                </div>
                <fieldset className="space-y-3">
                  <legend className="text-sm font-medium">Choose the stores you want to reach</legend>
                  <p className="text-sm text-muted-foreground">Leave all categories unchecked to reach all opted-in stores.</p>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {INDUSTRIES.map(industry => (
                      <label key={industry.value} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                        <Checkbox
                          checked={targetIndustryTypes.includes(industry.value)}
                          onCheckedChange={checked => setTargetIndustryTypes(current =>
                            checked
                              ? [...new Set([...current, industry.value])]
                              : current.filter(value => value !== industry.value),
                          )}
                        />
                        {industry.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                  <p className="max-w-3xl text-sm text-muted-foreground">
                    Pilot terms are confirmed separately. This app does not collect ad payments or share customer data.
                  </p>
                  <Button type="submit" disabled={createCampaign.isPending || isUploadingImages}>
                    {(createCampaign.isPending || isUploadingImages) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isUploadingImages ? "Uploading images…" : "Submit for review"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <section aria-labelledby="partner-campaigns-heading" className="space-y-3">
            <div>
              <h2 id="partner-campaigns-heading" className="text-xl font-semibold">Your submissions</h2>
              <p className="text-sm text-muted-foreground">Views and clicks count once per receiving store per UTC day.</p>
            </div>
            {campaigns.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">Your submitted offers will appear here.</div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                {campaigns.map(campaign => (
                  <Card key={campaign.id}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <CardTitle className="text-base">{campaign.title}</CardTitle>
                          <CardDescription className="mt-1">{industryLabel(campaign.advertiserCategory)} · {campaign.targetIndustryTypes.length ? campaign.targetIndustryTypes.map(industryLabel).join(", ") : "All opted-in stores"}</CardDescription>
                        </div>
                        <Badge variant={statusVariant(campaign.status)}>{statusLabel(campaign)}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {campaign.imageUrls.length > 0 && (
                        <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label={`Flyer images for ${campaign.title}`}>
                          {campaign.imageUrls.map((imageUrl, index) => (
                            <img
                              key={imageUrl}
                              src={imageUrl}
                              alt={`Flyer image ${index + 1} for ${campaign.title}`}
                              className="aspect-[16/7] w-full rounded-md border bg-muted object-contain"
                              loading="lazy"
                              decoding="async"
                            />
                          ))}
                        </div>
                      )}
                      <p className="text-sm">{campaign.description}</p>
                      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                        <span>{campaign.viewCount} views</span><span>{campaign.clickCount} clicks</span>
                        <span>{format(new Date(campaign.startsAt), "MMM d, yyyy")} – {format(new Date(campaign.endsAt), "MMM d, yyyy")}</span>
                      </div>
                      {campaign.reviewNote && <p className="rounded-md bg-muted p-3 text-sm">Review note: {campaign.reviewNote}</p>}
                      <a className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline" href={campaign.destinationUrl} target="_blank" rel="noopener noreferrer">
                        Preview destination <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Only approved, in-schedule offers can be shown. A view is a participating store dashboard impression; it does not represent a sale or conversion.
          </p>
        </>
      )}
    </main>
  );
}
